import { defineStore } from 'pinia'
import type { User } from '~/types'
import { clearSessionHint, hasSessionHint } from '~/utils/authSession'
import { resolveCurrentClientShopSlug } from '~/utils/resolveShopSlug'
import { resolveShopApiBase } from '~/utils/shopApi'
import { removeShopStorage } from '~/utils/shopStorage'

function currentShopSlug(): string {
  return resolveCurrentClientShopSlug()
}

function publicRuntimeConfig() {
  const nuxtApp = tryUseNuxtApp()
  const pub = nuxtApp?.$config?.public as Record<string, string> | undefined
  return {
    apiOrigin: String(pub?.apiOrigin || ''),
    platformApiUrl: String(pub?.platformApiUrl || pub?.apiUrl || ''),
  }
}

function clearLegacyTokenStorage(slug: string) {
  if (!import.meta.client) return
  removeShopStorage(slug, 'access_token')
  removeShopStorage(slug, 'refresh_token')
  removeShopStorage('', 'access_token')
  removeShopStorage('', 'refresh_token')
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  /** Session exists via httpOnly cookie; kept only as a soft client hint for refresh gating. */
  const hasSession = ref(false)
  const initialized = ref(false)
  const boundShop = ref('')

  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => {
    const role = user.value?.role
    return role === 'admin' || role === 'manager'
  })

  /** @deprecated use hasSession / cookies — kept so older callers compiling against accessToken keep working */
  const accessToken = computed(() => (hasSession.value || user.value ? 'cookie' : null))
  const refreshToken = computed(() => (hasSession.value || user.value ? 'cookie' : null))

  function hydrateFromStorage() {
    if (!import.meta.client) return
    const slug = currentShopSlug()
    if (!slug && (hasSession.value || user.value)) {
      return
    }
    boundShop.value = slug
    clearLegacyTokenStorage(slug)
    if (hasSessionHint()) {
      hasSession.value = true
    }
  }

  function setAuth(u: User) {
    user.value = u
    hasSession.value = true
    if (import.meta.client) {
      clearLegacyTokenStorage(currentShopSlug())
    }
  }

  function setUser(u: User) {
    user.value = u
    hasSession.value = true
  }

  /** No-op for cookie sessions — tokens are not stored in JS. */
  function setTokens(_access?: string, _refresh?: string) {
    hasSession.value = true
  }

  function clearAuth() {
    user.value = null
    hasSession.value = false
    if (import.meta.client) {
      const slug = currentShopSlug() || boundShop.value
      clearLegacyTokenStorage(slug)
      clearSessionHint()
    }
  }

  async function refreshTokens(): Promise<boolean> {
    try {
      const nuxtApp = tryUseNuxtApp()
      const api = nuxtApp?.$api as { post: (url: string, body?: unknown) => Promise<{ data: { success: boolean, data?: { user: User } } }> } | undefined
      if (api) {
        const { data } = await api.post('/auth/refresh', {})
        if (data?.success) {
          hasSession.value = true
          if (data.data?.user) {
            user.value = data.data.user
          }
          return true
        }
        return false
      }

      const config = publicRuntimeConfig()
      const domainState = nuxtApp
        ? useState<{ shop: { slug?: string } | null }>('shop-domain')
        : null
      const domainSlug = domainState?.value?.shop?.slug || ''
      const apiBase = resolveShopApiBase(currentShopSlug() || domainSlug, {
        apiOrigin: config.apiOrigin,
        platformApiUrl: config.platformApiUrl,
        sameOriginApi: Boolean(domainSlug),
      })
      const response = await $fetch<{ success: boolean, data?: { user: User } }>(
        `${apiBase}/auth/refresh`,
        {
          method: 'POST',
          body: {},
          credentials: 'include',
        },
      )

      if (response.success) {
        hasSession.value = true
        if (response.data?.user) {
          user.value = response.data.user
        }
        return true
      }
    } catch {
      clearAuth()
    }
    return false
  }

  function init() {
    if (!import.meta.client) return
    if (initialized.value) return
    hydrateFromStorage()
    initialized.value = true
  }

  init()

  return {
    user,
    accessToken,
    refreshToken,
    hasSession,
    initialized,
    isAuthenticated,
    isAdmin,
    setAuth,
    setUser,
    setTokens,
    clearAuth,
    refreshTokens,
    hydrateFromStorage,
  }
})
